module Client.Admin.UserInfoSections.Audits

open Client
open Elmish
open Shared.Admin.Audit
open Illy.SafeStack
open Illy.SafeStack.Client
open Shared
open Fable.React
open Fable.React.Props
open Illy.SafeStack.Client.jQueryUI
open Browser

type Model =
    {
        From: DatePicker.Model
        To: DatePicker.Model
        Operation: NoFlags MultiSelect.Model
        GettingData: bool
        GettingFirstPage: bool
        SearchString: Text.Model
        TotalCount: int 
        CanLoadMoreData: bool
        LastPage: int
        DebounceSearch: Option<string> Debounce.Model
        Audits: AuditEntry list
    }

type Msg =
    | LoadedAudits of AuditEntry list * int
    | ApiError of ApiError
    | CloseOverlay
    | SearchStringMsg of Text.Msg
    | DebounceSearchMsg of Option<string> Debounce.Msg
    | LoadMoreResults
    | RefreshGrid
    | FromMsg of DatePicker.Msg
    | ToMsg of DatePicker.Msg
    | OperationsMsg of MultiSelect.Msg
    | DownloadAsCsv
    | DownloadedCsv of unit

type Event =
    | ApiError of ApiError
    | CloseOverlay

module Initialisations =
    let createOperationList() =
        AuditOperation.iterate()
        |> List.map (fun x -> { Id = int x; Value = AuditOperation.format x; Group = "" }) 
        |> ListType<NoFlags>.Standard
    
    let operations selected = 
        MultiSelect.init 
            "operations" 
            "Operation:"
            None
            false
            (createOperationList())
            selected

let init() =
    {
        Audits = []
        Operation = Initialisations.operations []
        From = DatePicker.init "from" "From:" None false false (Some System.DateTime.Today)
        To = DatePicker.init "to" "To:" None false false (Some System.DateTime.Today)
        GettingData = false
        GettingFirstPage = false
        SearchString = Text.CustomSettings.defaultOptions() |> Text.init "search" "" (Some "Type to filter by username") 255. false None
        TotalCount = 0
        CanLoadMoreData = false
        LastPage = 1
        DebounceSearch = Debounce.init (System.TimeSpan.FromMilliseconds 300.) None
    }, Cmd.none

let modelToFilter model search =
    {
        From = model.From |> DatePicker.toOption |> Option.defaultValue System.DateTime.Today
        To = model.To |> DatePicker.toOption |> Option.defaultValue System.DateTime.Today
        Operations = model.Operation.SelectedOptions |> List.map (fun x -> (ListOptionType.unpackStandard x).Id |> enum<AuditOperation>)
        Username = search
    }

let loadAuditsCmd model page search =
    let filter =  modelToFilter model search
    Api.secureCall<IUserInfoApi,_,_,_> SecuredRoute.Admin (fun api -> api.listAuditsPaged filter) page LoadedAudits Msg.ApiError

let showGrid model =
    {
        model with
            GettingData = true
            GettingFirstPage = true
    }, loadAuditsCmd model 1 None

let update msg model =
    match msg with
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event
    | Msg.ApiError err -> model, err |> ApiError |> SideEffect.Event
    | DownloadAsCsv ->
        let filter =  modelToFilter model (model.SearchString |> Text.toString)
        model, Api.downloadFile<IUserInfoApi,_,_> SecuredRoute.Admin (fun api -> api.downloadAudits) filter DownloadedCsv Msg.ApiError |> SideEffect.Cmd
    | DownloadedCsv _ -> model,SideEffect.none
    | FromMsg fMsg ->
        let dMod, dCmd = DatePicker.update model.From fMsg
        { model with From = dMod },
        Cmd.batch [
            yield Cmd.map FromMsg dCmd 
            if model.From.Value <> dMod.Value then yield Cmd.ofMsg RefreshGrid
        ] |> SideEffect.Cmd
    | ToMsg fMsg ->
        let dMod, dCmd = DatePicker.update model.To fMsg
        { model with To = dMod},
        Cmd.batch [
            yield Cmd.map ToMsg dCmd 
            if model.To.Value <> dMod.Value then yield Cmd.ofMsg RefreshGrid
        ] |> SideEffect.Cmd
    | OperationsMsg opMsg ->
        let opMod, opCmd = MultiSelect.update opMsg model.Operation
        { model with Operation = opMod},
        Cmd.batch [
            yield Cmd.map OperationsMsg opCmd 
            if model.Operation.SelectedOptions <> opMod.SelectedOptions then yield Cmd.ofMsg RefreshGrid
        ] |> SideEffect.Cmd
    | LoadedAudits (audits, count) ->
        { model with
            Audits = if model.GettingFirstPage then audits else model.Audits @ audits 
            GettingFirstPage = false 
            GettingData = false
            TotalCount = count
            CanLoadMoreData = (model.LastPage) * Shared.Admin.UserGrid.PAGE_SIZE <= count
        }, SideEffect.none
    | SearchStringMsg cMsg ->
        let m, c = Text.update cMsg model.SearchString
        {model with SearchString = m},
        Cmd.batch [
            yield Cmd.map SearchStringMsg c
            if Text.toString m <> Text.toString model.SearchString then yield Debounce.inputCmd (Text.toString m) DebounceSearchMsg
        ] |> SideEffect.Cmd
    | DebounceSearchMsg dMsg -> 
        let m, c = 
            Debounce.updateWithCmd
                dMsg
                DebounceSearchMsg
                model.DebounceSearch
                (fun x -> 
                    { model with
                        DebounceSearch = x 
                    })
                (loadAuditsCmd model 1)
        m , SideEffect.Cmd c
    | RefreshGrid ->
        {model with 
            GettingData = true
            GettingFirstPage = true
            LastPage = 1
        }, model.SearchString |> Text.toString |> loadAuditsCmd model 1 |> SideEffect.Cmd
    | LoadMoreResults -> 
        if not model.CanLoadMoreData || model.GettingData then model, SideEffect.none
        else 
            let newPage =  model.LastPage + 1
            {model with
                GettingData = true 
                LastPage = newPage
            }, model.SearchString |> Text.toString |> loadAuditsCmd model newPage |> SideEffect.Cmd

let view (model: Model) dispatch =
    jQueryUI.modal
        "Audit Log"
        2500
        Msg.CloseOverlay
        dispatch
        (Some 1100)
        [
            div [
                Key "audit-search-container"
                ClassName "audit-search-container"
            ] [
                div [ ClassName "filters" ] [
                    DatePicker.date model.From (FromMsg >> dispatch) false []
                    DatePicker.date model.To (ToMsg >> dispatch) false []
                    MultiSelect.multiSelect false model.Operation (OperationsMsg >> dispatch)
                ]

                Text.textInput false model.SearchString (SearchStringMsg >> dispatch)
                div [ 
                    ClassName "download text" 
                    OnClick (fun _ -> dispatch DownloadAsCsv)
                ] [ str "Download as CSV" ]

                div [
                    Key "audit-search-results-container"
                    Id "audit-search-results-container"
                    Class "audit-search-results"
                    Style [
                        Height "300px"
                        OverflowY OverflowOptions.Scroll
                    ]
                    OnScroll (fun _ -> 
                        if not model.GettingData && model.CanLoadMoreData then
                            let parent = document.getElementById("audit-search-results-container")
                            if (parent.scrollHeight - parent.scrollTop - parent.clientHeight) <= 1. then
                                debounce
                                    500
                                    (fun (changeEv,_) -> dispatch changeEv)
                                    LoadMoreResults
                                    ()
                    )
                ] [
                    yield jQueryUI.simpleTable 
                        "There are no changes audited with the current search parameters. "
                        [
                            Some [ str "Performed by" ] ===> fun (x: AuditEntry) -> [ str x.PerformedBy ]
                            Some [ str "Date Logged" ] ===> fun (x: AuditEntry) -> [ str <| illyDateTime x.DateLogged ]
                            Some [ str "Message" ] ===> fun (x: AuditEntry) -> [ str x.Message ]
                        ]
                        model.GettingData
                        model.Audits

                    if model.GettingData && not model.GettingFirstPage then 
                        yield jQueryUI.spinner
                ]
            ]
        ]
